import React, {Component} from 'react';
import {connect} from 'react-redux';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelToken,
  CancelTokenSource,
} from '../../../../../helpers/asyncCancelHelpers';
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
} from '../../../../../helpers/asyncDataHelpers';
import {makeMapStateToProps} from '../../../../../helpers/reselectHelpers';
import {ActivityModel} from '../../../../../models/api/activityModel';
import {ApiUploadedAttachments, AttachmentModel} from '../../../../../models/api/attachmentModel';
import {ChannelModel} from '../../../../../models/api/channelModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {InboxModel} from '../../../../../models/api/inboxModel';
import {ApiSeenByList, SeenByModel} from '../../../../../models/api/seenByModel';
import {fetchApiAsync} from '../../../../../services/http/http';
import {AppState} from '../../../../../store/appState';
import {Dispatch} from '../../../../../store/appStore';
import {fetchPinnedActivitiesAsyncAction} from '../../../../../store/data/activities/activitiesActions';
import {getCompanyHost} from '../../../../../store/data/company/companySelectors';
import {
  makeGetConversationChannels,
  makeGetConversationInboxes,
  makeGetConversationPinnedActivities,
} from '../../../../../store/data/conversations/conversationsSelectors';
import {FindTeammate, getFindTeammate} from '../../../../../store/data/teammates/lazyTeammatesSelectors';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {ConversationSummary} from './conversationSummary';
import {ConversationSummarySectionsEnum} from './conversationSummarySections';

/*
 * Props + State.
 */

interface ConversationSummaryContainerPublicProps {
  panelId: ViewerPanelId;
  conversation: ConversationModel;
  onRequestClose: Handler;
  teammateId: number;
  initiallyExpandedSection?: ConversationSummarySectionsEnum;
}

interface ConversationSummaryContainerProps extends ConversationSummaryContainerPublicProps {
  dispatch: Dispatch;
  host: string;
  inboxes: ReadonlyArray<InboxModel>;
  channels: ReadonlyArray<ChannelModel>;
  findTeammate: FindTeammate;
  pinnedActivities: ReadonlyArray<ActivityModel>;
}

interface ConversationSummaryContainerState {
  pinnedActivitiesAsyncData: AsyncData<ReadonlyArray<ActivityModel>>;
  attachments: AsyncData<ReadonlyArray<AttachmentModel>>;
  seenBy: AsyncData<ReadonlyArray<SeenByModel>>;
}

/*
 * Component.
 */

class ConversationSummaryContainerComponent extends Component<
  ConversationSummaryContainerProps,
  ConversationSummaryContainerState
> {
  private readonly fetchCancelSource: CancelTokenSource;
  private get cancelToken(): CancelToken {
    return this.fetchCancelSource.token;
  }

  constructor(props: ConversationSummaryContainerProps) {
    super(props);

    this.fetchCancelSource = buildCancelTokenSource();

    this.state = {
      pinnedActivitiesAsyncData: {status: AsyncStatusesEnum.LOADING},
      attachments: {status: AsyncStatusesEnum.LOADING},
      seenBy: {status: AsyncStatusesEnum.LOADING},
    };
  }

  /*
   * Lifecycle.
   */

  componentDidMount() {
    this.fetchPinnedActivitiesAsync();
    this.fetchSeenByAsync();
    this.fetchAttachmentsAsync();
  }

  componentWillUnmount() {
    this.fetchCancelSource.cancel();
  }

  /*
   * Network fetches.
   */

  private readonly fetchPinnedActivitiesAsync = async () => {
    this.setState({pinnedActivitiesAsyncData: asyncDataLoading()});

    try {
      await this.props.dispatch(fetchPinnedActivitiesAsyncAction(this.props.conversation, this.cancelToken));

      // Actual pinnedActivities are added to pinnedActivitiesAsyncData in `buildPinnedActivities` after retrieving from Redux.
      this.setState({pinnedActivitiesAsyncData: asyncDataLoaded([])});
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      this.setState({pinnedActivitiesAsyncData: asyncDataFailed(error)});
    }
  };

  private readonly buildPinnedActivities = (): AsyncData<ReadonlyArray<ActivityModel>> => {
    if (this.state.pinnedActivitiesAsyncData.status !== AsyncStatusesEnum.LOADED) {
      return this.state.pinnedActivitiesAsyncData;
    }

    return {
      status: AsyncStatusesEnum.LOADED,
      data: this.props.pinnedActivities,
    };
  };

  private readonly fetchAttachmentsAsync = async () => {
    this.setState({attachments: asyncDataLoading()});

    try {
      const path = `${this.props.conversation.url}/attachments`;
      const attachmentsData = await fetchApiAsync(ApiUploadedAttachments, path, {
        cancelToken: this.cancelToken,
      });

      this.setState({attachments: asyncDataLoaded(attachmentsData)});
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      this.setState({attachments: asyncDataFailed(error)});
    }
  };

  private readonly fetchSeenByAsync = async () => {
    this.setState({seenBy: asyncDataLoading()});

    try {
      const path = `${this.props.conversation.url}/seen_by`;
      const seenByData = await fetchApiAsync(ApiSeenByList, path, {cancelToken: this.cancelToken});

      this.setState({seenBy: asyncDataLoaded(seenByData)});
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      this.setState({seenBy: asyncDataFailed(error)});
    }
  };

  /*
   * Render.
   */

  render() {
    return (
      <ConversationSummary
        panelId={this.props.panelId}
        teammateId={this.props.teammateId}
        onRequestClose={this.props.onRequestClose}
        conversation={this.props.conversation}
        host={this.props.host}
        inboxes={this.props.inboxes}
        channels={this.props.channels}
        findTeammate={this.props.findTeammate}
        initiallyExpandedSection={this.props.initiallyExpandedSection}
        attachments={this.state.attachments}
        seenBy={this.state.seenBy}
        onSeenByRetryClick={this.fetchSeenByAsync}
        onAttachmentsRetryClick={this.fetchAttachmentsAsync}
        pinnedActivities={this.buildPinnedActivities()}
        onPinnedActivitiesRetryClick={this.fetchPinnedActivitiesAsync}
      />
    );
  }
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationSummaryContainerPublicProps>()(() => {
  const getConversation = (_state: AppState, props: ConversationSummaryContainerPublicProps) =>
    props.conversation;

  return {
    inboxes: makeGetConversationInboxes(getConversation),
    channels: makeGetConversationChannels(getConversation),
    pinnedActivities: makeGetConversationPinnedActivities(getConversation),
    host: getCompanyHost,
    findTeammate: getFindTeammate,
  };
});

export const ConversationSummaryContainer = connect(mapStateToProps)(ConversationSummaryContainerComponent);
